'use client'
import { Accordion } from '@dustin-web/microsite-components'
import { Checkbox } from '@dustin-web/microsite-components/src/atoms'
import { useTranslations } from '@dustin-web/microsite-components/src/hooks'
import QuantitySelector from '@dustin-web/microsite-components/src/quantity-selector'
import { usePathname } from 'next/navigation'
import { Card } from '@dustin-web/product-components/src/cards/product-card-gallery-composable'
import { useContext, useState } from 'react'
import classNames from 'classnames'
import React from 'react'
import { AttachedSalesContext } from '../product-page/product-information/attached-sales-provider'
import UmbrellaIcon from '@dustin-web/microsite-components/src/icon/generated/umbrella'
import './attached-sales-accordion.scss'

type AttachedSalesAccordionProps = {
  list: any[]
  updateQuantity: (id: string, quantity: number) => void
  handleIsSelected: (id: string) => void
  mainProductQuantity: number
  productsToAddToCart: any[]
  showMore?: boolean
}

export default function AttachedSalesAccordion({
  list,
  updateQuantity,
  handleIsSelected,
  mainProductQuantity,
  productsToAddToCart,
  showMore,
}: AttachedSalesAccordionProps) {
  const pathname = usePathname()
  const { translate } = useTranslations()
  const [isAccordionOpen, setIsAccordionOpen] = useState(false)
  const totalQuantitySelected = productsToAddToCart
    .map(item => item.quantity)
    .reduce((accumulator, currentValue) => accumulator + currentValue, 0)

  const accordionTitle =
    totalQuantitySelected && !isAccordionOpen ? (
      <div className="d-flex flex-column u-fw-bold">
        {translate('choose_additional_insurances_and_warranties')}
        <span className="u-fw-normal u-fs-12">
          {totalQuantitySelected}{' '}
          {translate(
            totalQuantitySelected > 1
              ? 'insurances_and_warranties_multiple_selected'
              : 'insurances_and_warranties_single_selected'
          )}
        </span>
      </div>
    ) : (
      <div className="u-fw-bold">{translate('choose_additional_insurances_and_warranties')}</div>
    )

  const searchParams = new URLSearchParams({
    tab: 'accessories',
    subTab: 'header_productnearservices',
  })

  return (
    <div className="u-py-32">
      <Accordion
        buttonId={
          //gtm
          isAccordionOpen
            ? 'attached-sales-accordion-button-closed'
            : 'attached-sales-accordion-button-open'
        }
        noPanelPadding
        variant="default"
        iconComponent={<UmbrellaIcon name="umbrella" className="i-20" />}
        className="u-border u-border-black attached-sales-accordion"
        buttonClassName="d-flex"
        handleAccordionIsOpen={isOpen => setIsAccordionOpen(isOpen)}
        title={accordionTitle}>
        <div className="attached-sales-list ">
          {list.map((item, key) => (
            <React.Fragment key={key}>
              <AttachedSalesCard
                handleIsSelected={handleIsSelected}
                updateQuantity={updateQuantity}
                quantity={item.quantity}
                key={key}
                selectedParentQuantity={mainProductQuantity}
                digitalDataIndex={key}
                isSelected={item.isSelected}
                id={item.productErpIdentifier}
                product={item.product}
              />
              {key > 0 && <div className="attached-sales-divider"></div>}
            </React.Fragment>
          ))}
          {showMore && (
            <a
              className="attached-sales-accordion__showMoreLink"
              id="show_more_insurances hej" //gtm
              href={pathname + '?' + searchParams.toString()}>
              {translate('show_more')}
            </a>
          )}
        </div>
      </Accordion>
    </div>
  )
}

type AttachedSalesCardProps = {
  id: string
  handleIsSelected: AttachedSalesAccordionProps['handleIsSelected']
  digitalDataIndex: number
  updateQuantity: AttachedSalesAccordionProps['updateQuantity']
  selectedParentQuantity: number
  isSelected: boolean
  product: AttachedSalesAccordionProps['list'][0]
  quantity: number
}
const AttachedSalesCard = ({
  id,
  handleIsSelected,
  digitalDataIndex,
  updateQuantity,
  selectedParentQuantity,
  isSelected,
  product,
  quantity,
}: AttachedSalesCardProps) => {
  const minQuantity =
    product.minQuantityPerOrder && product.minQuantityPerOrder > 0 ? product.minQuantityPerOrder : 1

  return (
    <div className={classNames('attached-sales-card', {})}>
      <div className="attached-sales-card__inner">
        <div className="attached-sales-card__left">
          <div className="attached-sales-card__left__checkbox">
            <Check
              handleIsSelected={() => handleIsSelected(id)}
              checked={isSelected}
              productName={product.displayName}
            />
          </div>
          <div className="attached-sales-card__left__content">
            <div className="attached-sales-card__left__content__title">
              <Card.Title
                digitalDataIndex={digitalDataIndex}
                digitalDataOrigin="productPage"
                product={{ ...product, manufacturerName: '' }}
                ellipsis
                compact
                ticket=""
              />
            </div>
            {product.promotedSpecifications && (
              <div className="attached-sales-card__left__content__promotedSpec">
                <Card.SpecificationList slim={false} list={product.promotedSpecifications} />
              </div>
            )}
          </div>
        </div>
        <div className="attached-sales-card__right">
          <div className="attached-sales-card__right__price">
            <Card.Price
              isAvailableForSale
              price={product.price}
              displayVatInfo
              priceClassName="u-fs-14"
              className="c-product-card-price--row align-items-right align-items-end d-flex flex-direction-column"
            />
          </div>
          <Quantity
            productName={product.displayName}
            quantity={quantity}
            handleQuantity={newQuantity => updateQuantity(id, newQuantity)}
            minQuantity={minQuantity}
            selectedParentQuantity={selectedParentQuantity}
            hide={selectedParentQuantity < 2 || !isSelected}
          />
        </div>
      </div>
    </div>
  )
}

type QuantityProps = {
  productName: string
  quantity: AttachedSalesCardProps['quantity']
  handleQuantity: (newQuantity: any) => void
  minQuantity: number
  selectedParentQuantity: number
  hide: boolean
}
const Quantity = ({
  productName,
  quantity,
  handleQuantity,
  minQuantity,
  selectedParentQuantity,
  hide,
}: QuantityProps) => {
  function changeQuantity(newQuantity: number) {
    if (newQuantity <= selectedParentQuantity) {
      handleQuantity(newQuantity)
    }
  }
  if (selectedParentQuantity < quantity) {
    handleQuantity(selectedParentQuantity)
  }
  if (hide) return null

  return (
    <div className="attached-sales-card__right__quantity-selector">
      <QuantitySelector
        productName={productName}
        quantity={quantity}
        setQuantity={newQuantity => changeQuantity(newQuantity)}
        minQuantity={minQuantity}
        maxQuantity={selectedParentQuantity}
        displayValidationMessage={false}
      />
    </div>
  )
}
const Check = ({
  handleIsSelected,
  checked,
  productName,
}: {
  handleIsSelected: (event: any) => void
  checked: boolean
  productName: string
}) => {
  return (
    <Checkbox
      id={!checked ? 'selected' : 'unselected'} //gtm
      classes={productName} //gtm
      checked={checked}
      onChange={handleIsSelected}
    />
  )
}

export function AttachedSalesAccordionWithContext({
  moreProductNearServicesExist,
}: {
  moreProductNearServicesExist: boolean
}) {
  const { list, productsToAddToCart, updateQuantity, handleIsSelected, mainProductQuantity } =
    useContext(AttachedSalesContext)
  return (
    <AttachedSalesAccordion
      list={list}
      productsToAddToCart={productsToAddToCart}
      updateQuantity={updateQuantity}
      handleIsSelected={handleIsSelected}
      mainProductQuantity={mainProductQuantity}
      showMore={moreProductNearServicesExist}
    />
  )
}
